import { PROPERTY_TYPES, GID_MASK } from '../utils/constants.js';

const PROP_COLORS = {
  [PROPERTY_TYPES.COLLISION]: 'rgba(255, 0, 0, 0.3)',
  [PROPERTY_TYPES.OCCLUSION]: 'rgba(0, 100, 255, 0.3)',
  [PROPERTY_TYPES.WATER]: 'rgba(0, 200, 255, 0.3)',
};

/**
 * Overlay that renders property visualizations (collision, occlusion, water)
 * and per-tile collision shapes from the tileset.
 */
export class OverlayRenderer {
  render(ctx, state, viewport) {
    if (!state.get('showProperties')) return;

    const layers = state.get('layers');
    const mapW = state.get('mapWidth');
    const mapH = state.get('mapHeight');
    const tileW = state.get('tileWidth');
    const tileH = state.get('tileHeight');

    // Visible tile range (cull to viewport)
    const b = viewport.tileBounds || { minTileX: 0, minTileY: 0, maxTileX: mapW - 1, maxTileY: mapH - 1 };
    const xStart = Math.max(0, b.minTileX);
    const xEnd = Math.min(mapW - 1, b.maxTileX);
    const yStart = Math.max(0, b.minTileY);
    const yEnd = Math.min(mapH - 1, b.maxTileY);

    // Per-cell property overlays (from PropertyBrush)
    for (const layer of layers) {
      if (layer.type !== 'tilelayer' || !layer.visible) continue;
      const props = layer.properties || {};

      for (const [propType, color] of Object.entries(PROP_COLORS)) {
        const propData = props[propType];
        if (!propData || !Array.isArray(propData.value)) continue;

        ctx.fillStyle = color;
        const data = propData.value;
        for (let y = yStart; y <= yEnd; y++) {
          for (let x = xStart; x <= xEnd; x++) {
            if (data[y * mapW + x]) {
              ctx.fillRect(x * tileW, y * tileH, tileW, tileH);
            }
          }
        }
      }
    }

    // Per-tile collision shapes (from tileset TileCollisionEditor)
    const tileCollisions = state.get('tileCollisions');
    if (!tileCollisions || tileCollisions.size === 0) return;

    ctx.strokeStyle = 'rgba(255, 100, 0, 0.6)';
    ctx.fillStyle = 'rgba(255, 100, 0, 0.15)';
    ctx.lineWidth = 1;

    for (const layer of layers) {
      if (layer.type !== 'tilelayer' || !layer.visible || !layer.data) continue;

      for (let y = yStart; y <= yEnd; y++) {
        for (let x = xStart; x <= xEnd; x++) {
          const gid = layer.data[y * mapW + x];
          if (gid <= 0) continue;

          const tileId = gid & GID_MASK;
          const shapes = tileCollisions.get(tileId);
          if (!shapes) continue;

          const px = x * tileW;
          const py = y * tileH;
          const scaleX = tileW / (state.getTilesetForGid(tileId)?.tileWidth || tileW);
          const scaleY = tileH / (state.getTilesetForGid(tileId)?.tileHeight || tileH);

          for (const shape of shapes) {
            if (shape.type === 'rect') {
              ctx.fillRect(
                px + shape.x * scaleX, py + shape.y * scaleY,
                shape.width * scaleX, shape.height * scaleY
              );
              ctx.strokeRect(
                px + shape.x * scaleX, py + shape.y * scaleY,
                shape.width * scaleX, shape.height * scaleY
              );
            } else if (shape.type === 'polygon' && shape.points.length > 0) {
              ctx.beginPath();
              const ox = (shape.x || 0) * scaleX;
              const oy = (shape.y || 0) * scaleY;
              ctx.moveTo(px + ox + shape.points[0].x * scaleX, py + oy + shape.points[0].y * scaleY);
              for (let p = 1; p < shape.points.length; p++) {
                ctx.lineTo(px + ox + shape.points[p].x * scaleX, py + oy + shape.points[p].y * scaleY);
              }
              ctx.closePath();
              ctx.fill();
              ctx.stroke();
            }
          }
        }
      }
    }
  }
}
